import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, RotateCcw, Tag } from "lucide-react";
import { toast } from "sonner";

export default function PrijslijstTab({ orgId }) {
  const [producten, setProducten] = useState([]);
  const [customMap, setCustomMap] = useState({}); // product_id -> {verkoopprijs, klantcode}
  const [drafts, setDrafts] = useState({}); // product_id -> input string
  const [codeDrafts, setCodeDrafts] = useState({}); // product_id -> klantcode string
  const [q, setQ] = useState("");

  const load = async () => {
    const [{ data: pr }, { data: cs }] = await Promise.all([
      api.get("/producten"),
      api.get(`/organisaties/${orgId}/prijzen`),
    ]);
    setProducten(pr);
    const map = {};
    cs.forEach(r => { map[r.product_id] = r; });
    setCustomMap(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return producten.filter(p =>
      !q || p.naam.toLowerCase().includes(term) || (p.sku || "").toLowerCase().includes(term)
        || (p.productgroep || "").toLowerCase().includes(term)
    );
  }, [producten, q]);

  const setDraft = (pid, v) => setDrafts(d => ({ ...d, [pid]: v }));
  const setCodeDraft = (pid, v) => setCodeDrafts(d => ({ ...d, [pid]: v }));

  const save = async (p) => {
    const raw = drafts[p.id];
    if (raw === undefined || raw === "") return; // niets gewijzigd
    const num = parseFloat(String(raw).replace(",", "."));
    if (isNaN(num) || num < 0) { toast.error("Ongeldige prijs"); return; }
    try {
      await api.put(`/organisaties/${orgId}/prijzen/${p.id}`, { verkoopprijs: num });
      toast.success(`Klantprijs voor ${p.naam} = ${fmtEUR(num)}`);
      setDrafts(d => { const c = { ...d }; delete c[p.id]; return c; });
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const saveCode = async (p) => {
    const raw = codeDrafts[p.id];
    if (raw === undefined) return; // niet aangeraakt
    const huidig = customMap[p.id]?.klantcode || "";
    if (raw.trim() === huidig) { setCodeDrafts(d => { const c = { ...d }; delete c[p.id]; return c; }); return; }
    try {
      await api.put(`/organisaties/${orgId}/prijzen/${p.id}`, { klantcode: raw.trim() });
      toast.success(raw.trim() ? `Klantcode voor ${p.naam} = ${raw.trim()}` : "Klantcode gewist");
      setCodeDrafts(d => { const c = { ...d }; delete c[p.id]; return c; });
      load();
    } catch { toast.error("Opslaan mislukt"); }
  };

  const reset = async (p) => {
    if (!window.confirm(`Klantprijs én klantcode voor ${p.naam} verwijderen en terug naar standaard?`)) return;
    try {
      await api.delete(`/organisaties/${orgId}/prijzen/${p.id}`);
      toast.success("Teruggezet op standaard");
      setDrafts(d => { const c = { ...d }; delete c[p.id]; return c; });
      setCodeDrafts(d => { const c = { ...d }; delete c[p.id]; return c; });
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const aangepast = Object.keys(customMap).length;

  return (
    <div className="space-y-3" data-testid="prijslijst-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground">
          Klantspecifieke prijzen &amp; codes — de klantcode vervangt onze code op de pakbon en factuur van deze organisatie.
        </div>
        <Badge variant="outline" className="ml-auto"><Tag className="h-3 w-3 mr-1" /> {aangepast} afwijkend(e)</Badge>
      </div>
      <div className="bg-card border border-border rounded-xl p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek product op naam, SKU of groep…" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="prijs-search" />
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left p-3">Product</th>
              <th className="text-left p-3">Klantcode</th>
              <th className="text-right p-3">Standaard</th>
              <th className="text-right p-3">Klantprijs</th>
              <th className="text-right p-3">Verschil</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map(p => {
              const cust = customMap[p.id];
              const heeftKlantprijs = !!cust && cust.verkoopprijs !== undefined && cust.verkoopprijs !== null;
              const heeftCode = !!cust?.klantcode;
              const currentKlant = heeftKlantprijs ? cust.verkoopprijs : null;
              const draft = drafts[p.id];
              const displayValue = draft !== undefined ? draft : (heeftKlantprijs ? String(currentKlant).replace(".", ",") : "");
              const codeDraft = codeDrafts[p.id];
              const codeValue = codeDraft !== undefined ? codeDraft : (cust?.klantcode || "");
              const std = Number(p.verkoopprijs || 0);
              const verschil = heeftKlantprijs ? (Number(currentKlant) - std) : null;
              return (
                <tr key={p.id} data-testid={`prijs-row-${p.id}`} className={(heeftKlantprijs || heeftCode) ? "bg-primary/[0.03]" : ""}>
                  <td className="p-3">
                    <div className="font-medium">{p.naam}</div>
                    <div className="text-xs text-muted-foreground font-mono">{p.sku} {p.productgroep ? `· ${p.productgroep}` : ""}</div>
                  </td>
                  <td className="p-3">
                    <Input type="text" placeholder="—"
                      value={codeValue}
                      onChange={(e)=>setCodeDraft(p.id, e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === "Enter") saveCode(p); }}
                      onBlur={()=>saveCode(p)}
                      className="w-32 h-8 font-mono text-xs"
                      data-testid={`klantcode-input-${p.id}`} />
                  </td>
                  <td className="p-3 text-right text-muted-foreground">{fmtEUR(std)}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-xs text-muted-foreground">€</span>
                      <Input type="text" inputMode="decimal" placeholder="—"
                        value={displayValue}
                        onChange={(e)=>setDraft(p.id, e.target.value)}
                        onKeyDown={(e)=>{ if (e.key === "Enter") save(p); }}
                        onBlur={()=>save(p)}
                        className="w-24 text-right h-8 tabular-nums"
                        data-testid={`prijs-input-${p.id}`} />
                    </div>
                  </td>
                  <td className={`p-3 text-right text-xs ${verschil !== null && verschil < 0 ? "text-destructive" : verschil !== null && verschil > 0 ? "text-accent" : "text-muted-foreground"}`}>
                    {verschil === null ? "—" : `${verschil > 0 ? "+" : ""}${fmtEUR(verschil)}`}
                  </td>
                  <td className="p-3 text-right">
                    {(heeftKlantprijs || heeftCode) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={()=>reset(p)} title="Terug naar standaard (prijs + code)"
                              data-testid={`prijs-reset-${p.id}`}>
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">Geen producten gevonden.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
